// src/components/DeltaPreview.js
// Component for previewing delta (.supr) files with base snapshot composition

import React, { useState, useEffect } from 'react';
import { Box, LinearProgress, Typography, Alert } from '@mui/material';

const DeltaPreview = ({ file, token, onBlobLoaded }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Compositing strokes...');

  useEffect(() => {
    const abortController = new AbortController();

    const loadComposite = async () => {
      if (!file || !file.path.endsWith('.supr')) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setProgress(0);
        setProgressMessage('Compositing strokes...');

        // Single HTTP POST with NDJSON streaming response for progress
        // No WebSocket needed — progress streams on the same connection
        const response = await fetch(
          'https://drive.suprsketch.com/api/fs/composite-delta',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              deltaPath: file.path,
              bucket: file.bucket
            }),
            signal: abortController.signal
          }
        );

        if (!response.ok) {
          let serverMessage = null;
          try {
            const errorBody = await response.json();
            serverMessage = errorBody && errorBody.error;
          } catch (bodyParseError) {
            // Error body wasn't JSON (or was empty); no server message to surface
          }
          const httpError = new Error(`HTTP error! status: ${response.status}`);
          httpError.status = response.status;
          httpError.serverMessage = serverMessage;
          throw httpError;
        }

        // Read NDJSON stream line by line
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let resultData = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // Keep incomplete line in buffer

          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const parsed = JSON.parse(line);
              if (parsed.type === 'progress') {
                setProgress(parsed.percentage);
                setProgressMessage(parsed.message);
              } else if (parsed.type === 'result') {
                resultData = parsed;
              } else if (parsed.type === 'error') {
                const streamError = new Error(parsed.details || parsed.error);
                streamError.code = parsed.code;
                streamError.serverMessage = parsed.error || parsed.details;
                throw streamError;
              }
            } catch (parseError) {
              // If it's a rethrown error from above, propagate it
              if (parseError.message && !parseError.message.includes('JSON')) {
                throw parseError;
              }
              console.error('Error parsing NDJSON line:', parseError);
            }
          }
        }

        if (!resultData) {
          throw new Error('No result received from composite stream');
        }

        // Create data URL from base64 image
        setImageData(`data:image/png;base64,${resultData.compositedImage}`);
        setMetadata({
          baseSnapshot: resultData.baseSnapshot,
          deltasApplied: resultData.deltasApplied,
          timestamp: resultData.timestamp
        });

        // Convert base64 to blob for download functionality
        if (onBlobLoaded) {
          try {
            const byteCharacters = atob(resultData.compositedImage);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/png' });
            onBlobLoaded(blob);
          } catch (blobError) {
            console.error('Error creating blob for download:', blobError);
          }
        }

        setLoading(false);
      } catch (err) {
        // Don't set error state if the request was intentionally aborted
        if (err.name === 'AbortError') return;

        if (err.status === 429) {
          setError(err.serverMessage || 'Another preview is being prepared. Wait a moment and click the file again.');
        } else if (err.status === 413 || err.code === 'CHAIN_TOO_LARGE') {
          setError(err.serverMessage || 'This part of the canvas history is too heavy to preview right now.');
        } else {
          setError('Failed to composite delta file. Please try again.');
        }
        setLoading(false);
      }
    };

    loadComposite();

    // Cleanup function - abort in-flight request
    return () => {
      abortController.abort();
      setImageData(null);
      setMetadata(null);
    };
  }, [file, token]);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        gap: 2,
        px: 4
      }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            width: '100%',
            maxWidth: '400px',
            height: 6,
            borderRadius: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#00B4D8' // Cyan color for delta compositing
            }
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {progressMessage} ({progress}%)
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!imageData) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
      }}>
        <Typography color="text.secondary">
          No preview available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      overflow: 'hidden',
      gap: 1
    }}>
      <img
        src={imageData}
        alt="Composited Preview"
        style={{
          maxWidth: '100%',
          maxHeight: '90%',
          objectFit: 'contain'
        }}
      />
      {metadata && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            px: 1
          }}
        >
          Base: {metadata.baseSnapshot} • Strokes: {metadata.deltasApplied}
        </Typography>
      )}
    </Box>
  );
};

export default DeltaPreview;
